import React, { useCallback } from 'react';

import {
  calculateInvestmentStockSplitUnitsAfter,
  calculateInvestmentStockSplitUnitsBefore,
  calculateInvestmentStockSplitUnitsFromAfter,
  calculateInvestmentStockSplitUnitsFromBefore,
} from 'app-components/forms/investment/investmentTransactionFormDisplay';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

import { investmentTransactionEditFormHooks } from 'components/Forms/InvestmentTransactionEditForm/investmentTransactionEditFormHooks';
import { SecurityAndTagsFields } from './SecurityAndTagsFields';
import { handleIntegerKeyDown, handleNumericKeyDown, sanitizeNumericValue, useNumericTextField } from './utils';

export function StockSplitFields(): React.JSX.Element {
  const splitNumerator = investmentTransactionEditFormHooks.useField('splitNumerator');
  const splitDenominator = investmentTransactionEditFormHooks.useField('splitDenominator');
  const units = investmentTransactionEditFormHooks.useField('units');
  const setUnits = units.onValueChange;
  const splitState = {
    splitDenominator: splitDenominator.value,
    splitNumerator: splitNumerator.value,
    units: units.value,
  };
  const unitsBefore = calculateInvestmentStockSplitUnitsBefore(splitState);
  const unitsAfter = calculateInvestmentStockSplitUnitsAfter(splitState);
  const splitUnchanged = splitNumerator.value === splitDenominator.value;

  const setUnitsBefore = useCallback(
    (newUnitsBefore: number | null) => {
      setUnits(
        newUnitsBefore == null
          ? null
          : (calculateInvestmentStockSplitUnitsFromBefore({
              splitDenominator: splitDenominator.value,
              splitNumerator: splitNumerator.value,
              units: newUnitsBefore,
            }) ?? 0),
      );
    },
    [setUnits, splitDenominator.value, splitNumerator.value],
  );
  const setUnitsAfter = useCallback(
    (newUnitsAfter: number | null) => {
      setUnits(
        newUnitsAfter == null
          ? null
          : (calculateInvestmentStockSplitUnitsFromAfter({
              splitDenominator: splitDenominator.value,
              splitNumerator: splitNumerator.value,
              units: newUnitsAfter,
            }) ?? 0),
      );
    },
    [setUnits, splitDenominator.value, splitNumerator.value],
  );

  const unitsAfterInput = useNumericTextField({ value: unitsAfter, onValueChange: setUnitsAfter });
  const unitsBeforeInput = useNumericTextField({ value: unitsBefore, onValueChange: setUnitsBefore });

  return (
    <>
      <SecurityAndTagsFields />
      <Box display='flex' gap={3} flexDirection='column'>
        <Box display='flex' alignItems='center' gap={1}>
          <TextField
            id='split-numerator'
            label='Post Split'
            type='number'
            inputProps={{ min: 1, inputMode: 'numeric' }}
            sx={{ minWidth: 110 }}
            value={splitNumerator.value ?? ''}
            onKeyDown={handleIntegerKeyDown}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const sanitized = sanitizeNumericValue(event.target.value, { allowDecimal: false });
              splitNumerator.onValueChange(sanitized ? Math.abs(Number(sanitized)) : null);
            }}
            error={splitNumerator.error}
            helperText={splitNumerator.helperText}
          />
          :
          <TextField
            id='split-denominator'
            label='Pre Split'
            type='number'
            inputProps={{ min: 1, inputMode: 'numeric' }}
            sx={{ minWidth: 110 }}
            value={splitDenominator.value ?? ''}
            onKeyDown={handleIntegerKeyDown}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const sanitized = sanitizeNumericValue(event.target.value, { allowDecimal: false });
              splitDenominator.onValueChange(sanitized ? Math.abs(Number(sanitized)) : null);
            }}
            error={splitDenominator.error}
            helperText={splitDenominator.helperText}
          />
        </Box>
        <Box display='flex' alignItems='center' gap={1}>
          <TextField
            id='units-after'
            label='Units After Split'
            type='number'
            inputProps={{ min: 1, inputMode: 'decimal' }}
            sx={{ minWidth: 110 }}
            disabled={splitUnchanged}
            value={unitsAfterInput.value}
            onKeyDown={handleNumericKeyDown}
            onChange={unitsAfterInput.onChange}
          />
          -
          <TextField
            id='units-before'
            label='Units Before Split'
            type='number'
            inputProps={{ min: 1, inputMode: 'decimal' }}
            sx={{ minWidth: 110 }}
            disabled={splitUnchanged}
            value={unitsBeforeInput.value}
            onKeyDown={handleNumericKeyDown}
            onChange={unitsBeforeInput.onChange}
          />
          =
          <TextField
            id='units'
            label='Shares adjustment'
            type='number'
            disabled
            sx={{ minWidth: 110 }}
            value={!splitUnchanged && units.value != null ? Math.abs(units.value) : ''}
          />
        </Box>
      </Box>
    </>
  );
}
